import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ExcelJS from 'exceljs';
import Layout from '../components/layout/Layout';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type ActionFilter =
  | 'all'
  | 'grn.approved'
  | 'grn.rejected'
  | 'dispatch.approved'
  | 'dispatch.rejected'
  | 'dispatch.security_approved'
  | 'grn-item.deleted'
  | 'dispatch.item.deleted'
  | 'grn.past_update.approved'
  | 'grn.past_update.rejected'
  | 'dispatch.past_update.approved'
  | 'dispatch.past_update.rejected';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  'grn.approved':              { label: 'GRN Approved',       color: 'bg-green-100 text-green-800' },
  'grn.rejected':              { label: 'GRN Rejected',        color: 'bg-red-100 text-red-800' },
  'dispatch.approved':         { label: 'Dispatch Approved',   color: 'bg-brand-100 text-brand-800' },
  'dispatch.rejected':         { label: 'Dispatch Rejected',   color: 'bg-red-100 text-red-800' },
  'dispatch.security_approved':{ label: 'Security Approved',   color: 'bg-purple-100 text-purple-800' },
  'grn-item.deleted':                 { label: 'GRN Item Deleted',        color: 'bg-orange-100 text-orange-800' },
  'dispatch.item.deleted':            { label: 'Dispatch Item Deleted',   color: 'bg-orange-100 text-orange-800' },
  'grn.past_update.approved':         { label: 'Past GRN Edit Approved',  color: 'bg-green-100 text-green-800' },
  'grn.past_update.rejected':         { label: 'Past GRN Edit Rejected',  color: 'bg-red-100 text-red-800' },
  'dispatch.past_update.approved':    { label: 'Past Dispatch Edit Approved', color: 'bg-brand-100 text-brand-800' },
  'dispatch.past_update.rejected':    { label: 'Past Dispatch Edit Rejected', color: 'bg-red-100 text-red-800' },
};

export default function AuditReportPage() {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('audit-report:export');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingEmployee, setIsExportingEmployee] = useState(false);
  const limit = 50;

  const summaryQuery = useQuery({
    queryKey: ['audit-summary', dateFrom, dateTo],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (dateFrom) p.append('dateFrom', dateFrom);
      if (dateTo)   p.append('dateTo',   dateTo);
      const res = await api.get(`/audit-report/summary?${p.toString()}`);
      return res?.data as {
        employees: Array<{
          employeeId: number | null;
          employeeName: string | null;
          employeeEmail: string | null;
          approved: number;
          rejected: number;
          securityApproved: number;
          total: number;
        }>;
        totals: { approved: number; rejected: number; securityApproved: number; total: number };
      };
    },
    staleTime: 0,
  });

  const actionsQuery = useQuery({
    queryKey: ['audit-actions', dateFrom, dateTo, actionFilter, page],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (dateFrom)                p.append('dateFrom', dateFrom);
      if (dateTo)                  p.append('dateTo',   dateTo);
      if (actionFilter !== 'all')  p.append('action',   actionFilter);
      p.append('page',  page.toString());
      p.append('limit', limit.toString());
      const res = await api.get(`/audit-report?${p.toString()}`);
      return res?.data as {
        items: Array<{
          id: number;
          action: string;
          entityType: string;
          entityId: number;
          actionTime: string;
          employeeName: string | null;
          employeeEmail: string | null;
          submitterName: string | null;
          submitterEmail: string | null;
          referenceNumber: string | null;
          clientName: string | null;
          rejectionReason: string | null;
          deletedItemName: string | null;
        }>;
        total: number;
      };
    },
    staleTime: 0,
  });

  const totalPages = Math.ceil((actionsQuery.data?.total ?? 0) / limit);

  const totals = summaryQuery.data?.totals;
  const employees = summaryQuery.data?.employees ?? [];

  const kpis = useMemo(() => [
    { label: 'Total Actions',      value: totals?.total           ?? 0, color: 'from-brand-500 to-brand-600',   icon: '📋' },
    { label: 'Approved',           value: totals?.approved        ?? 0, color: 'from-green-500 to-green-600', icon: '✅' },
    { label: 'Rejected',           value: totals?.rejected        ?? 0, color: 'from-red-500 to-red-600',     icon: '❌' },
    { label: 'Security Approved',  value: totals?.securityApproved ?? 0, color: 'from-purple-500 to-purple-600', icon: '🔒' },
  ], [totals]);

  const displayName = (name: string | null, email: string | null) =>
    name || email?.split('@')[0] || 'Unknown';

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return d; }
  };

  // Shared by the desktop tables and the mobile card lists
  const employeeColumns: TableColumn[] = [
    { key: 'employeeName', label: 'Employee', render: (_, emp) => displayName(emp.employeeName, emp.employeeEmail) },
    { key: 'employeeEmail', label: 'Email', render: (value) => value ?? '—' },
    {
      key: 'approved',
      label: 'GRN Approved',
      render: (value) => <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{value}</span>,
    },
    {
      key: 'rejected',
      label: 'GRN / Dispatch Rejected',
      render: (value) => <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{value}</span>,
    },
    {
      key: 'dispatchApproved',
      label: 'Dispatch Approved',
      render: (_, emp) => <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">{emp.approved}</span>,
    },
    {
      key: 'securityApproved',
      label: 'Security Approved',
      render: (value) => <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{value}</span>,
    },
    { key: 'total', label: 'Total', render: (value) => <span className="font-semibold text-gray-900">{value}</span> },
  ];

  const actionLogColumns: TableColumn[] = [
    { key: 'seq', label: '#', render: (value) => <span className="text-gray-400 text-xs">{value}</span> },
    {
      key: 'employeeName',
      label: 'Action Taken By',
      render: (_, row) => (
        <div>
          <div className="font-medium text-gray-900">{displayName(row.employeeName, row.employeeEmail)}</div>
          {row.employeeEmail && <div className="text-xs text-gray-400">{row.employeeEmail}</div>}
        </div>
      ),
    },
    {
      key: 'submitterName',
      label: 'Submitted By',
      render: (_, row) => (
        <div>
          <div className="font-medium text-gray-700">{displayName(row.submitterName, row.submitterEmail)}</div>
          {row.submitterEmail && <div className="text-xs text-gray-400">{row.submitterEmail}</div>}
        </div>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => {
        const actionMeta = ACTION_LABELS[value] ?? { label: value, color: 'bg-gray-100 text-gray-700' };
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${actionMeta.color}`}>
            {actionMeta.label}
          </span>
        );
      },
    },
    { key: 'entityType', label: 'Type', render: (value) => <span className="text-gray-600 uppercase text-xs font-medium">{value}</span> },
    { key: 'referenceNumber', label: 'Reference', render: (value) => <span className="font-mono text-xs text-brand-700">{value ?? '—'}</span> },
    { key: 'clientName', label: 'Client', render: (value) => value ?? '—' },
    { key: 'actionTime', label: 'Date & Time', render: (value) => <span className="text-gray-500 text-xs">{fmtDate(value)}</span> },
    {
      key: 'deletedItemName',
      label: 'Deleted Item',
      render: (value) =>
        value ? (
          <span className="text-orange-700 text-xs bg-orange-50 px-2 py-1 rounded">{value}</span>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        ),
    },
    {
      key: 'rejectionReason',
      label: 'Rejection Reason',
      render: (value) =>
        value ? (
          <span className="text-red-700 text-xs bg-red-50 px-2 py-1 rounded">{value}</span>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        ),
    },
  ];

  // Mobile card data for the action log includes the running row number
  const actionLogCardData = (actionsQuery.data?.items ?? []).map((row, i) => ({
    ...row,
    seq: (page - 1) * limit + i + 1,
  }));

  const downloadBlob = (buffer: ArrayBuffer, filename: string) => {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const styleHeaderRow = (row: ExcelJS.Row, headerColor: string) => {
    row.height = 32;
    row.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColor } };
      cell.font   = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11, name: 'Calibri' };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top:    { style: 'medium', color: { argb: headerColor } },
        bottom: { style: 'medium', color: { argb: headerColor } },
        left:   { style: 'thin',   color: { argb: 'FFFFFFFF' } },
        right:  { style: 'thin',   color: { argb: 'FFFFFFFF' } },
      };
    });
  };

  const styleDataRow = (row: ExcelJS.Row, index: number, evenColor: string) => {
    row.height = 22;
    const bg = index % 2 === 0 ? 'FFFFFFFF' : evenColor;
    row.eachCell({ includeEmpty: true }, cell => {
      cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.font      = { size: 10, name: 'Calibri' };
      cell.alignment = { vertical: 'middle' };
      cell.border    = {
        top:    { style: 'hair', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'hair', color: { argb: 'FFD1D5DB' } },
        left:   { style: 'hair', color: { argb: 'FFD1D5DB' } },
        right:  { style: 'hair', color: { argb: 'FFD1D5DB' } },
      };
    });
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom)               p.append('dateFrom', dateFrom);
      if (dateTo)                 p.append('dateTo',   dateTo);
      if (actionFilter !== 'all') p.append('action',   actionFilter);
      p.append('page', '1'); p.append('limit', '99999');
      const res = await api.get(`/audit-report?${p.toString()}`);
      const items: Array<{
        action: string; entityType: string; actionTime: string;
        employeeName: string | null; employeeEmail: string | null;
        submitterName: string | null; submitterEmail: string | null;
        referenceNumber: string | null; clientName: string | null;
        rejectionReason: string | null; deletedItemName: string | null;
      }> = res.data.items;

      const wb = new ExcelJS.Workbook();
      wb.creator = 'Warehouse Management'; wb.created = new Date();
      const ws = wb.addWorksheet('Action Log', { views: [{ state: 'frozen', ySplit: 1 }] });

      ws.columns = [
        { key: 'no',              header: '#',                    width: 6  },
        { key: 'actionBy',        header: 'Action Taken By',      width: 24 },
        { key: 'actionByEmail',   header: 'Action Taken By Email',width: 30 },
        { key: 'submittedBy',     header: 'Submitted By',         width: 24 },
        { key: 'submittedByEmail',header: 'Submitted By Email',   width: 30 },
        { key: 'action',          header: 'Action',               width: 22 },
        { key: 'type',            header: 'Type',                 width: 12 },
        { key: 'reference',       header: 'Reference',            width: 16 },
        { key: 'client',          header: 'Client',               width: 24 },
        { key: 'dateTime',        header: 'Date & Time',          width: 24 },
        { key: 'deletedItem',     header: 'Deleted Item',         width: 26 },
        { key: 'rejectionReason', header: 'Rejection Reason',     width: 32 },
      ];

      styleHeaderRow(ws.getRow(1), 'FF4F46E5'); // indigo-600
      ws.autoFilter = { from: 'A1', to: 'L1' };

      items.forEach((row, i) => {
        const dr = ws.addRow({
          no:               i + 1,
          actionBy:         displayName(row.employeeName, row.employeeEmail),
          actionByEmail:    row.employeeEmail ?? '',
          submittedBy:      displayName(row.submitterName, row.submitterEmail),
          submittedByEmail: row.submitterEmail ?? '',
          action:           ACTION_LABELS[row.action]?.label ?? row.action,
          type:             row.entityType.toUpperCase(),
          reference:        row.referenceNumber ?? '',
          client:           row.clientName ?? '',
          dateTime:         fmtDate(row.actionTime),
          deletedItem:      row.deletedItemName ?? '',
          rejectionReason:  row.rejectionReason ?? '',
        });
        styleDataRow(dr, i, 'FFEEF2FF'); // light indigo stripe
      });

      downloadBlob(await wb.xlsx.writeBuffer(), `audit-report${dateFrom ? `-${dateFrom}` : ''}${dateTo ? `-to-${dateTo}` : ''}.xlsx`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportEmployeeCSV = async () => {
    setIsExportingEmployee(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = 'Warehouse Management'; wb.created = new Date();
      const ws = wb.addWorksheet('Employee Productivity', { views: [{ state: 'frozen', ySplit: 1 }] });

      ws.columns = [
        { key: 'employee',        header: 'Employee',                   width: 26 },
        { key: 'email',           header: 'Email',                      width: 32 },
        { key: 'grnApproved',     header: 'GRN Approved',               width: 16 },
        { key: 'rejected',        header: 'GRN / Dispatch Rejected',    width: 26 },
        { key: 'dispatchApproved',header: 'Dispatch Approved',          width: 20 },
        { key: 'securityApproved',header: 'Security Approved',          width: 20 },
        { key: 'total',           header: 'Total',                      width: 12 },
      ];

      styleHeaderRow(ws.getRow(1), 'FF16A34A'); // green-600
      ws.autoFilter = { from: 'A1', to: 'G1' };

      employees.forEach((emp, i) => {
        const dr = ws.addRow({
          employee:         displayName(emp.employeeName, emp.employeeEmail),
          email:            emp.employeeEmail ?? '',
          grnApproved:      emp.approved,
          rejected:         emp.rejected,
          dispatchApproved: emp.approved,
          securityApproved: emp.securityApproved,
          total:            emp.total,
        });
        styleDataRow(dr, i, 'FFF0FDF4'); // light green stripe

        // Bold total column
        const totalCell = dr.getCell('total');
        totalCell.font = { bold: true, size: 10, name: 'Calibri' };
        totalCell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Summary totals row
      if (totals) {
        const sumRow = ws.addRow({
          employee: 'TOTAL', email: '',
          grnApproved: totals.approved, rejected: totals.rejected,
          dispatchApproved: totals.approved, securityApproved: totals.securityApproved,
          total: totals.total,
        });
        sumRow.height = 26;
        sumRow.eachCell({ includeEmpty: true }, cell => {
          cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF166534' } };
          cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11, name: 'Calibri' };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.border    = {
            top:    { style: 'medium', color: { argb: 'FF166534' } },
            bottom: { style: 'medium', color: { argb: 'FF166534' } },
            left:   { style: 'thin',   color: { argb: 'FFFFFFFF' } },
            right:  { style: 'thin',   color: { argb: 'FFFFFFFF' } },
          };
        });
      }

      downloadBlob(await wb.xlsx.writeBuffer(), `employee-productivity${dateFrom ? `-${dateFrom}` : ''}${dateTo ? `-to-${dateTo}` : ''}.xlsx`);
    } finally {
      setIsExportingEmployee(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Audit Report</h1>
                <p className="text-gray-600 mt-0.5">Track employee approval/rejection activity and productivity</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Action</label>
            <ResponsiveSelect
              value={actionFilter}
              onChange={(val) => { setActionFilter(val as ActionFilter); setPage(1); }}
              includeEmpty={false}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
              options={[
                { value: 'all', label: 'All Actions' },
                { value: 'grn.approved', label: 'GRN Approved' },
                { value: 'grn.rejected', label: 'GRN Rejected' },
                { value: 'dispatch.approved', label: 'Dispatch Approved' },
                { value: 'dispatch.rejected', label: 'Dispatch Rejected' },
                { value: 'dispatch.security_approved', label: 'Security Approved' },
                { value: 'grn-item.deleted', label: 'GRN Item Deleted' },
                { value: 'dispatch.item.deleted', label: 'Dispatch Item Deleted' },
                { value: 'grn.past_update.approved', label: 'Past GRN Edit Approved' },
                { value: 'grn.past_update.rejected', label: 'Past GRN Edit Rejected' },
                { value: 'dispatch.past_update.approved', label: 'Past Dispatch Edit Approved' },
                { value: 'dispatch.past_update.rejected', label: 'Past Dispatch Edit Rejected' },
              ]}
            />
          </div>
          {(dateFrom || dateTo || actionFilter !== 'all') && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); setActionFilter('all'); setPage(1); }}
              className="text-sm text-gray-500 hover:text-red-600 border border-gray-300 rounded-lg px-3 py-2 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {kpis.map(kpi => (
            <div key={kpi.label} className={`bg-gradient-to-br ${kpi.color} rounded-xl p-4 text-white shadow-md`}>
              <div className="text-2xl mb-1">{kpi.icon}</div>
              <div className="text-3xl font-bold">{kpi.value}</div>
              <div className="text-sm opacity-90 mt-1">{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Employee Productivity */}
        <div className="bg-white rounded-xl shadow border border-gray-200 mb-6 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h2 className="text-sm font-semibold text-gray-900">Employee Productivity</h2>
              <span className="text-xs text-gray-500">({employees.length} employees)</span>
            </div>
            {canExport && (
              <button
                onClick={handleExportEmployeeCSV}
                disabled={isExportingEmployee || employees.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isExportingEmployee ? 'Exporting...' : 'Export Excel'}
              </button>
            )}
          </div>
          {summaryQuery.isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
          ) : employees.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No activity found for this period.</div>
          ) : (
            <>
            <div className="p-3 lg:hidden">
              <MobileCardList columns={employeeColumns} data={employees} />
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Employee', 'Email', 'GRN Approved', 'GRN / Dispatch Rejected', 'Dispatch Approved', 'Security Approved', 'Total'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employees.map((emp, i) => (
                    <tr key={i} className="hover:bg-brand-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{displayName(emp.employeeName, emp.employeeEmail)}</td>
                      <td className="px-4 py-3 text-gray-500">{emp.employeeEmail ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{emp.approved}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{emp.rejected}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">{emp.approved}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{emp.securityApproved}</span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{emp.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Detailed Action Log */}
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <h2 className="text-sm font-semibold text-gray-900">Action Log</h2>
              {actionsQuery.data && (
                <span className="text-xs text-gray-500">({actionsQuery.data.total} records)</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canExport && (
                <button
                  onClick={handleExportCSV}
                  disabled={isExporting || !actionsQuery.data?.total}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  {isExporting ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  {isExporting ? 'Exporting...' : 'Export Excel'}
                </button>
              )}
              {/* Pagination */}
              {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  Prev
                </button>
                <span className="text-xs text-gray-600">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
            </div>
          </div>

          {actionsQuery.isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading action log...</div>
          ) : !actionsQuery.data?.items.length ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-gray-400 text-sm">No audit records found. Approval/rejection actions will appear here.</p>
            </div>
          ) : (
            <>
            <div className="p-3 lg:hidden">
              <MobileCardList columns={actionLogColumns} data={actionLogCardData} />
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['#', 'Action Taken By', 'Submitted By', 'Action', 'Type', 'Reference', 'Client', 'Date & Time', 'Deleted Item', 'Rejection Reason'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {actionsQuery.data.items.map((row, i) => {
                    const actionMeta = ACTION_LABELS[row.action] ?? { label: row.action, color: 'bg-gray-100 text-gray-700' };
                    return (
                      <tr key={row.id} className={`hover:bg-brand-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/40'}`}>
                        <td className="px-4 py-3 text-gray-400 text-xs">{(page - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{displayName(row.employeeName, row.employeeEmail)}</div>
                          {row.employeeEmail && <div className="text-xs text-gray-400">{row.employeeEmail}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-700">{displayName(row.submitterName, row.submitterEmail)}</div>
                          {row.submitterEmail && <div className="text-xs text-gray-400">{row.submitterEmail}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${actionMeta.color}`}>
                            {actionMeta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 uppercase text-xs font-medium">{row.entityType}</td>
                        <td className="px-4 py-3 font-mono text-xs text-brand-700">{row.referenceNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{row.clientName ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(row.actionTime)}</td>
                        <td className="px-4 py-3 max-w-xs">
                          {row.deletedItemName ? (
                            <span className="text-orange-700 text-xs bg-orange-50 px-2 py-1 rounded">{row.deletedItemName}</span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {row.rejectionReason ? (
                            <span className="text-red-700 text-xs bg-red-50 px-2 py-1 rounded">{row.rejectionReason}</span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

      </div>
    </Layout>
  );
}
